"use client"

import { CheckCircle2, AlertCircle, Info, Loader2, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

export type AppToastType = "success" | "error" | "info" | "loading" | "default"

export type AppToastProps = {
  title:         string
  description?:  string
  type?:         AppToastType
  /** Auto-dismiss timeout. Null/0 → no auto dismiss. */
  duration?:     number | null
  onDismiss?:    () => void
  /** Called when hover starts/ends — host pauses its timer accordingly. */
  onHoverStart?: () => void
  onHoverEnd?:   () => void
}

// Per-type styling — Aperture-Light tones to match the rest of the app.
const TONES: Record<AppToastType, {
  iconBg: string
  iconBorder: string
  iconColor: string
  bar: string
  hairline: string
}> = {
  success: {
    iconBg:    "#DFF5E5",
    iconBorder:"rgba(15,81,50,0.25)",
    iconColor: "#0F5132",
    bar:       "linear-gradient(90deg, #1F7A4E, #0F5132)",
    hairline:  "rgba(15,81,50,0.45)",
  },
  error: {
    iconBg:    "rgba(160,43,31,0.10)",
    iconBorder:"rgba(160,43,31,0.30)",
    iconColor: "#A02B1F",
    bar:       "linear-gradient(90deg, #C4523F, #A02B1F)",
    hairline:  "rgba(160,43,31,0.45)",
  },
  info: {
    iconBg:    "rgba(45,74,138,0.10)",
    iconBorder:"rgba(45,74,138,0.28)",
    iconColor: "#2D4A8A",
    bar:       "linear-gradient(90deg, #4A6BB0, #2D4A8A)",
    hairline:  "rgba(45,74,138,0.45)",
  },
  loading: {
    iconBg:    "#DFF5E5",
    iconBorder:"rgba(15,81,50,0.25)",
    iconColor: "#0F5132",
    bar:       "linear-gradient(90deg, #1F7A4E, #34D399, #1F7A4E)",
    hairline:  "rgba(15,81,50,0.45)",
  },
  default: {
    iconBg:    "rgba(10,10,10,0.06)",
    iconBorder:"rgba(10,10,10,0.10)",
    iconColor: "rgba(10,10,10,0.62)",
    bar:       "linear-gradient(90deg, rgba(10,10,10,0.30), rgba(10,10,10,0.55))",
    hairline:  "rgba(10,10,10,0.20)",
  },
}

const ICONS = {
  success: CheckCircle2,
  error:   AlertCircle,
  info:    Info,
  loading: Loader2,
  default: Info,
} as const

/**
 * Toast card — Aperture-Light editorial surface.
 *
 * Visual:
 *  - Cream surface, mint hairline at the top (same cue every card uses)
 *  - Tinted icon badge per type (mint / rust / ink-blue / mint pulsing)
 *  - Title + optional description, ink-on-cream contrast
 *  - Bottom progress bar shrinks left → right while the timer runs
 *  - Hovering pauses the bar (and the parent's auto-dismiss timer is
 *    expected to follow via onDismiss being delayed by the host)
 *  - X button on top-right (always visible — no hidden affordances)
 */
export function AppToast({
  title,
  description,
  type = "default",
  duration,
  onDismiss,
  onHoverStart,
  onHoverEnd,
}: AppToastProps) {
  const tone = TONES[type]
  const Icon = ICONS[type]
  const [paused, setPaused] = useState(false)

  // Track elapsed time so the progress bar shrinks linearly. We render
  // a CSS transform animated by JS rAF; pausing on hover freezes the
  // visual without canceling the parent's setTimeout — the parent owns
  // the actual dismissal, this is just the visual cue.
  const progressRef = useRef<HTMLDivElement | null>(null)
  const startedAt   = useRef<number>(Date.now())
  const pausedAt    = useRef<number | null>(null)
  const accumPaused = useRef<number>(0)
  const rafRef      = useRef<number | null>(null)

  useEffect(() => {
    if (!duration || duration <= 0) return

    const tick = () => {
      const now = Date.now()
      const pausedMs = accumPaused.current + (paused && pausedAt.current ? (now - pausedAt.current) : 0)
      const elapsed  = now - startedAt.current - pausedMs
      const pct = Math.max(0, Math.min(1, elapsed / duration))
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${1 - pct})`
      }
      if (pct < 1) rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration])

  useEffect(() => {
    if (paused) {
      pausedAt.current = Date.now()
    } else if (pausedAt.current) {
      accumPaused.current += Date.now() - pausedAt.current
      pausedAt.current = null
    }
  }, [paused])

  return (
    <div
      role="status"
      onMouseEnter={() => { setPaused(true); onHoverStart?.() }}
      onMouseLeave={() => { setPaused(false); onHoverEnd?.() }}
      className="relative overflow-hidden rounded-2xl group"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(10,10,10,0.08)",
        boxShadow:
          "0 1px 0 rgba(255,255,255,0.65) inset, 0 18px 44px -22px rgba(10,10,10,0.28), 0 0 50px -28px rgba(15,81,50,0.18)",
      }}
    >
      {/* Mint hairline at the very top — same editorial cue */}
      <div
        aria-hidden
        className="absolute inset-x-5 top-0 h-px pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, ${tone.hairline}, transparent)`,
        }}
      />

      <div className="relative flex items-start gap-3 px-4 py-3.5 pr-9">
        {/* Tinted icon badge */}
        <div
          className="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg"
          style={{
            background: tone.iconBg,
            border: `1px solid ${tone.iconBorder}`,
          }}
        >
          <Icon
            size={15}
            strokeWidth={2}
            style={{ color: tone.iconColor }}
            className={type === "loading" ? "animate-spin" : undefined}
          />
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          <p
            className="leading-snug"
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: "#0A0A0A",
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </p>
          {description && (
            <p
              className="mt-0.5 leading-relaxed break-words"
              style={{ fontSize: 12, color: "rgba(10,10,10,0.62)" }}
            >
              {description}
            </p>
          )}
        </div>

        {/* Dismiss — appears on hover but accessible via keyboard always */}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="absolute top-2.5 right-2.5 flex h-6 w-6 items-center justify-center rounded-md transition-all duration-150 opacity-40 group-hover:opacity-100"
            style={{ color: "rgba(10,10,10,0.55)" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(10,10,10,0.06)" }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
          >
            <X size={12} strokeWidth={2.4} />
          </button>
        )}
      </div>

      {/* Progress bar — only for non-loading toasts with a real duration */}
      {type !== "loading" && duration && duration > 0 && (
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[2px] overflow-hidden"
          style={{ background: "rgba(10,10,10,0.04)" }}
        >
          <div
            ref={progressRef}
            className="h-full origin-left"
            style={{
              background: tone.bar,
              transform: "scaleX(1)",
              willChange: "transform",
            }}
          />
        </div>
      )}

      {/* Loading indeterminate bar — sweeps L→R continuously */}
      {type === "loading" && (
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[2px] overflow-hidden"
          style={{ background: "rgba(10,10,10,0.04)" }}
        >
          <div
            className="h-full w-1/3"
            style={{
              background: tone.bar,
              animation: "toast-loader-sweep 1.4s ease-in-out infinite",
              willChange: "transform",
            }}
          />
          <style>{`
            @keyframes toast-loader-sweep {
              0%   { transform: translateX(-100%); }
              100% { transform: translateX(400%); }
            }
          `}</style>
        </div>
      )}
    </div>
  )
}
